import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DeviceMobile, ShareNetwork, PlusSquare, DotsThreeVertical, X, Download } from "@phosphor-icons/react";
import { isStandalone, isIOS, isIOSSafari, isAndroid, canPromptInstall, promptInstall, subscribeInstall } from "@/lib/install";
import { useAuth } from "@/context/AuthContext";

const DISMISSED_KEY = "bkpro_install_dismissed_at";
const REPROMPT_DAYS = 14;

/**
 * Cross-platform "Install BKflow as an app" experience.
 *
 * - Android / Chrome desktop: uses the native `beforeinstallprompt` event.
 * - iOS Safari: shows a modal with visual Share → "Sur l'écran d'accueil" steps.
 * - iOS non-Safari (Chrome/Firefox on iOS): asks the user to reopen in Safari.
 * - Already installed (standalone): renders nothing.
 */
export default function InstallAppPrompt() {
  const { user } = useAuth();
  const [banner, setBanner] = useState(false);
  const [modal, setModal] = useState(false);
  const [installable, setInstallable] = useState(canPromptInstall());

  useEffect(() => {
    const unsub = subscribeInstall(() => setInstallable(canPromptInstall()));
    return unsub;
  }, []);

  useEffect(() => {
    if (!user) return;
    if (isStandalone()) return; // already installed
    // Respect dismissal window
    try {
      const raw = localStorage.getItem(DISMISSED_KEY);
      if (raw) {
        const ts = new Date(raw).getTime();
        if (Date.now() - ts < REPROMPT_DAYS * 86400_000) return;
      }
    } catch (e) { /* ignore */ }
    // Show banner if iOS (needs manual instructions) or Android with a prompt captured
    const isIOSDevice = isIOS();
    if (isIOSDevice || installable) {
      // Small delay so it doesn't fight the push permission banner
      const t = setTimeout(() => setBanner(true), 4000);
      return () => clearTimeout(t);
    }
  }, [user, installable]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, new Date().toISOString()); } catch (e) { /* ignore */ }
    setBanner(false);
    setModal(false);
  };

  const openInstall = async () => {
    if (installable) {
      const outcome = await promptInstall();
      if (outcome === "accepted") {
        toast.success("BKflow installée sur votre écran d'accueil");
        setBanner(false);
      } else if (outcome === "dismissed") {
        dismiss();
      }
      return;
    }
    // iOS or non-installable Android → show the manual guide modal
    setModal(true);
  };

  if (isStandalone()) return null;
  if (!banner && !modal) return null;

  return (
    <>
      {banner && !modal && (
        <div
          className="fixed top-2 left-2 right-2 z-40 rounded-2xl border border-[#005CFF]/30 bg-[#0F1930]/95 backdrop-blur-lg p-3 flex items-center gap-3 shadow-lg animate-in fade-in slide-in-from-top-2"
          data-testid="install-prompt-banner"
        >
          <div className="w-9 h-9 rounded-xl bg-[#005CFF]/20 flex items-center justify-center shrink-0">
            <DeviceMobile size={18} weight="duotone" color="#7CB2FF" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">Installer BKflow</div>
            <div className="text-[11px] text-white/60 leading-tight">
              {isIOS() ? "Ajoutez l\u2019icône sur votre écran d\u2019accueil" : "Utilisez BKflow comme une vraie appli"}
            </div>
          </div>
          <button
            onClick={openInstall}
            className="text-xs font-semibold bg-[#005CFF] text-white px-3 py-1.5 rounded-full active:scale-95 shrink-0"
            data-testid="install-open-btn"
          >
            Installer
          </button>
          <button
            onClick={dismiss}
            className="w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-white/70 active:scale-95 shrink-0"
            aria-label="Fermer"
            data-testid="install-dismiss-btn"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      )}

      {modal && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 animate-in fade-in"
          onClick={dismiss}
          data-testid="install-modal-backdrop"
        >
          <div
            className="w-full max-w-md bg-[#0F1930] border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl animate-in slide-in-from-bottom-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="install-modal"
          >
            <div className="flex items-start gap-3 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-[#005CFF] flex items-center justify-center shrink-0 shadow-[0_0_30px_rgba(0,92,255,0.5)]">
                <Download size={22} weight="bold" color="white" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-lg leading-tight">Installer BKflow</div>
                <div className="text-xs text-white/50 mt-0.5">Accès direct depuis votre écran d&apos;accueil</div>
              </div>
              <button
                onClick={dismiss}
                className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white active:scale-95"
                aria-label="Fermer"
                data-testid="install-modal-close"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            {isIOS() ? (
              isIOSSafari() ? (
                <IOSSafariSteps />
              ) : (
                <IOSNonSafariHint />
              )
            ) : isAndroid() ? (
              <AndroidSteps />
            ) : (
              <DesktopSteps />
            )}

            <button
              onClick={dismiss}
              className="mt-6 w-full h-12 rounded-2xl bg-white/5 hover:bg-white/10 text-white/70 text-sm font-medium active:scale-[0.98] transition"
              data-testid="install-modal-later"
            >
              Plus tard
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-full bg-[#005CFF]/20 border border-[#005CFF]/40 flex items-center justify-center shrink-0 text-[#7CB2FF] text-xs font-bold">{n}</div>
      <div className="flex-1 text-sm text-white/85 leading-relaxed pt-0.5">{children}</div>
    </div>
  );
}

function IOSSafariSteps() {
  return (
    <div className="space-y-4">
      <Step n={1}>
        Appuyez sur le bouton <span className="inline-flex items-center gap-1 mx-1 px-2 py-0.5 rounded-md bg-white/10 border border-white/15 text-xs font-semibold"><ShareNetwork size={14} weight="bold" /> Partager</span>
        en bas de Safari.
      </Step>
      <Step n={2}>
        Faites défiler puis choisissez <span className="inline-flex items-center gap-1 mx-1 px-2 py-0.5 rounded-md bg-white/10 border border-white/15 text-xs font-semibold"><PlusSquare size={14} weight="bold" /> Sur l&apos;écran d&apos;accueil</span>.
      </Step>
      <Step n={3}>
        Confirmez avec <span className="mx-1 px-2 py-0.5 rounded-md bg-[#005CFF]/20 border border-[#005CFF]/40 text-xs font-semibold text-[#7CB2FF]">Ajouter</span>. BKflow apparaît alors sur votre écran d&apos;accueil, comme une vraie app.
      </Step>
    </div>
  );
}

function IOSNonSafariHint() {
  return (
    <div className="space-y-3 text-sm text-white/85">
      <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs">
        Vous utilisez Chrome / Firefox sur iOS. L&apos;installation ne fonctionne <b>que dans Safari</b>.
      </div>
      <Step n={1}>Copiez l&apos;adresse actuelle.</Step>
      <Step n={2}>Ouvrez <b>Safari</b>, collez l&apos;adresse et rechargez.</Step>
      <Step n={3}>Appuyez sur <ShareNetwork size={14} weight="bold" className="inline" /> Partager → <b>Sur l&apos;écran d&apos;accueil</b>.</Step>
    </div>
  );
}

function AndroidSteps() {
  return (
    <div className="space-y-4">
      <Step n={1}>
        Ouvrez le menu <span className="inline-flex items-center gap-1 mx-1 px-2 py-0.5 rounded-md bg-white/10 border border-white/15 text-xs font-semibold"><DotsThreeVertical size={14} weight="bold" /> Menu</span> en haut à droite de Chrome.
      </Step>
      <Step n={2}>
        Choisissez <span className="mx-1 px-2 py-0.5 rounded-md bg-white/10 border border-white/15 text-xs font-semibold">Ajouter à l&apos;écran d&apos;accueil</span> ou <span className="mx-1 px-2 py-0.5 rounded-md bg-white/10 border border-white/15 text-xs font-semibold">Installer l&apos;application</span>.
      </Step>
      <Step n={3}>
        Confirmez : l&apos;icône BKflow apparaît sur votre écran d&apos;accueil.
      </Step>
    </div>
  );
}

function DesktopSteps() {
  return (
    <div className="space-y-4">
      <Step n={1}>
        Cliquez sur l&apos;icône <span className="inline-flex items-center gap-1 mx-1 px-2 py-0.5 rounded-md bg-white/10 border border-white/15 text-xs font-semibold"><Download size={14} weight="bold" /> Installer</span> dans la barre d&apos;adresse de Chrome / Edge.
      </Step>
      <Step n={2}>
        Confirmez pour créer un raccourci dans votre menu Démarrer / Applications.
      </Step>
    </div>
  );
}
